import React from 'react';
import { Modal, ModalBody, ModalFooter } from 'reactstrap';
import { PropertyType } from '../propertyTypeEnum';
import {
    IAnticapteIssues,
    IAppalentInformation,
    IAppealAgainst,
    IBringingWitness,
    ICustomerInformation,
    IErrorSummary,
    ISDABForm,
    ISDABFormProps
} from '../types/buybox';
import ConfirmationModal from './confirmation-modal';
import { RichTextComponent } from '@msdyn365-commerce/core';

const ALL_PROVINCES = [
    'Alberta',
    'British Columbia',
    'Manitoba',
    'New Brunswick',
    'Newfoundland and Labrad',
    'Northwest and Territories',
    'Nunavut',
    'Nova Scotia',
    'Ontario',
    'Prince Edward Island',
    'Quebec',
    'Saskatchewan',
    'Yukon'
];

const SDABForm: React.FC<ISDABFormProps> = props => {
    const {
        sdabCusInfoFirstName,
        sdabCusInfoLastName,
        sdabCusInfoEmail,
        sdabCusInfoEmailConf,
        sdabCusInfoAddress1,
        sdabCusInfoAddress2,
        sdabCusInfoCity,
        sdabCusInfoProvince,
        sdabCusInfoPPostalCode,
        sdabCusInfoPCountry,
        sdabCusInfoPResidentialPhone,
        sdabSiteMunicipal,
        sdabSiteOtherDoc,
        sdabAgentNameLabel,
        requiredText,
        sdabFormOptYes,
        sdabFormHeader,
        closeModalLabel,
        sdabFormText,
        sdabInfoText,
        sdabCustomerInfoLabel,
        sdabSiteInfo,
        sdabSiteOtherDocPalaceHolder,
        sdabAppellantInfoLabel,
        sdabAppellantInfoTxtLbl,
        sdabAppellantInfoTxt,
        sdabSameInfoCheckBoxLabel,
        sdabCusInfoPPostalCodePlaceHolder,
        sdabCusInfoPResidentialPhonePlaceHolder,
        sdabAppellantAgainstLabel,
        sdabAppellantAgainstInfoLabel,
        sdabAppellantAgainstInfoTxt,
        sdabAppellantThDevelopmentPermit,
        sdabAppellantTdApproval,
        sdabAppellantTdConditionsApproval,
        sdabAppellantTdRefusal,
        sdabAppellantThSAA,
        sdabAppellantThNoticeOrder,
        sdabAppellantTdNoticeOrder,
        sdabReasonAppealLabel,
        sdabReasonAppealText1,
        sdabFormCharacterNum,
        sdabFormSchedulingText,
        sdabFormEstimateLabel,
        sdabFormEstimateOption30,
        sdabFormEstimateOption1h,
        sdabFormEstimateOption15h,
        sdabFormEstimateOption2h,
        sdabFormEstimateOption2PlusH,
        sdabFormLegalCounselLabel,
        sdabFormOptNo,
        sdabFormOptUnkown,
        sdabFormAnticipateText,
        sdabFormCharactersLabel,
        sdabFormAnticipateWitnessText,
        sdabFormBringingLabel,
        sdabFormDesclaimerText,
        addAppealToCartButtonLabel,
        requiredErrorSuffix,
        invalidErrorPrefix,
        matchErrorSuffix,
        appealReasonLengthError,
        formSubmissionAlert,
        iHereByText,
        underStoodText,
        infoProvidedAccurate,
        responsiblePayingAppeal
    } = props.restProps.resources;

    const customerInformationInitialData = {
        firstName: { value: '', errorMessage: '', label: sdabCusInfoFirstName, isRequired: true, section: 'customerInfo' },
        lastName: { value: '', errorMessage: '', label: sdabCusInfoLastName, isRequired: true, section: 'customerInfo' },
        email: { value: '', errorMessage: '', label: sdabCusInfoEmail, isRequired: true, section: 'customerInfo' },
        emailConfirm: { value: '', errorMessage: '', label: sdabCusInfoEmailConf, isRequired: true, section: 'customerInfo' },
        address1: { value: '', errorMessage: '', label: sdabCusInfoAddress1, isRequired: true, section: 'customerInfo' },
        address2: { value: '', errorMessage: '', label: sdabCusInfoAddress2, isRequired: false, section: 'customerInfo' },
        city: { value: '', errorMessage: '', label: sdabCusInfoCity, isRequired: true, section: 'customerInfo' },
        province: { value: 'Alberta', errorMessage: '', label: sdabCusInfoProvince, isRequired: true, section: 'customerInfo' },
        postalCode: { value: '', errorMessage: '', label: sdabCusInfoPPostalCode, isRequired: true, section: 'customerInfo' },
        residentialPhone: { value: '', errorMessage: '', label: sdabCusInfoPResidentialPhone, isRequired: true, section: 'customerInfo' },
        country: { value: 'Canada', errorMessage: '', label: sdabCusInfoPCountry, isRequired: true, section: 'customerInfo' },
        municipalAddress: { value: '', errorMessage: '', label: sdabSiteMunicipal, isRequired: true, section: 'siteInfo' },
        otherDocuments: { value: '', errorMessage: '', label: sdabSiteOtherDoc, isRequired: true, section: 'siteInfo' }
    };
    const appelantInformationInitialData = {
        firstName: { value: '', errorMessage: '', label: sdabCusInfoFirstName, isRequired: true, section: 'appelantInfo' },
        lastName: { value: '', errorMessage: '', label: sdabCusInfoLastName, isRequired: true, section: 'appelantInfo' },
        agentName: { value: '', errorMessage: '', label: sdabAgentNameLabel, isRequired: true, section: 'appelantInfo' },
        email: { value: '', errorMessage: '', label: sdabCusInfoEmail, isRequired: true, section: 'appelantInfo' },
        emailConfirm: { value: '', errorMessage: '', label: sdabCusInfoEmailConf, isRequired: true, section: 'appelantInfo' },
        address1: { value: '', errorMessage: '', label: sdabCusInfoAddress1, isRequired: true, section: 'appelantInfo' },
        address2: { value: '', errorMessage: '', label: sdabCusInfoAddress2, isRequired: false, section: 'appelantInfo' },
        city: { value: '', errorMessage: '', label: sdabCusInfoCity, isRequired: true, section: 'appelantInfo' },
        province: { value: 'Alberta', errorMessage: '', label: sdabCusInfoProvince, isRequired: true, section: 'appelantInfo' },
        postalCode: { value: '', errorMessage: '', label: sdabCusInfoPPostalCode, isRequired: true, section: 'appelantInfo' },
        residentialPhone: { value: '', errorMessage: '', label: sdabCusInfoPResidentialPhone, isRequired: true, section: 'appelantInfo' }
    };
    const appealAgainstInitialData = {
        appeal: { value: '', errorMessage: '', label: 'Appeal Type', isRequired: true, section: 'appealType' },
        reasonOfAppeal: { value: '', errorMessage: '', label: 'Reason for Appeal', isRequired: true, section: 'appealType' }, // 2000 characters
        presentationTime: { value: '30 minutes', errorMessage: '', label: 'Presentation Time', isRequired: true, section: 'appealType' },
        usingAgentOrLegalCounsel: { value: '', errorMessage: '', label: '', isRequired: false, section: 'appealType' }
    };
    const anticipateIssuesInitialData = {
        isAnticipateIssuesWithYourAppeal: { value: '', errorMessage: '', label: '', isRequired: false, section: 'issues' },
        isAnticipateIssuesWithYourAppealDescription: {
            value: '',
            errorMessage: '',
            label: 'Issues',
            isRequired: false,
            section: 'issues'
        } // only to ask if above one is set to YES
    };
    const bringingWitnessInitialData = {
        bringingWitness: { value: '', errorMessage: '', label: '', isRequired: false, section: 'issues' },
        howManyWitness: { value: '', errorMessage: '', label: 'Witnesses', isRequired: false, section: 'issues' }
    };
    const [isDisabled, setIsDisabled] = React.useState(false);
    const [customerInfo, setCustomerInfo] = React.useState<ICustomerInformation>(customerInformationInitialData);
    const [appelantInformation, setAppelantInformation] = React.useState<IAppalentInformation>(appelantInformationInitialData);
    const [appealAgainst, setAppealAgainst] = React.useState<IAppealAgainst>(appealAgainstInitialData);
    const [anticipateIssues, setAnticipateIssues] = React.useState<IAnticapteIssues>(anticipateIssuesInitialData);
    const [bringingWitness, setBringingWitness] = React.useState<IBringingWitness>(bringingWitnessInitialData);
    const [isUseCustomerInfoChecked, setIsUseCustomerInfoChecked] = React.useState(false);
    const [isConfirmationModalOpen, setIsConfirmationModalOpen] = React.useState(false);
    const { isModelOpen, toggleSDABModal, restProps, product } = props;

    React.useEffect(() => {
        if (isUseCustomerInfoChecked) {
            const newInfo = { ...customerInfo };
            for (const key in newInfo) {
                newInfo[key] = { ...newInfo[key], errorMessage: '' };
            }
            setAppelantInformation(prevState => {
                return {
                    ...prevState,
                    firstName: { ...newInfo.firstName },
                    lastName: { ...newInfo.lastName },
                    email: { ...newInfo.email },
                    emailConfirm: { ...newInfo.emailConfirm },
                    address1: { ...newInfo.address1 },
                    address2: { ...newInfo.address2 },
                    city: { ...newInfo.city },
                    province: { ...newInfo.province },
                    postalCode: { ...newInfo.postalCode },
                    residentialPhone: { ...newInfo.residentialPhone }
                };
            });
        }
    }, [isUseCustomerInfoChecked, customerInfo]);

    const handleCustomerInfo = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setCustomerInfo(prevState => {
            if ((!value || value.length <= 0) && prevState[name].isRequired) {
                // display error message
                return {
                    ...prevState,
                    [name]: { ...prevState[name], value, errorMessage: `${prevState[name].label}${requiredErrorSuffix}` }
                };
            } else {
                return {
                    ...prevState,
                    [name]: { ...prevState[name], value, errorMessage: '' }
                };
            }
        });
    };
    const handleAppelantInformation = (e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLSelectElement>) => {
        const { name, value } = e.target;
        setAppelantInformation(prevState => {
            if ((!value || value.length <= 0) && prevState[name].isRequired) {
                // update error message
                return {
                    ...prevState,
                    [name]: { ...prevState[name], value, errorMessage: `${prevState[name].label} ${requiredErrorSuffix}` }
                };
            } else {
                return {
                    ...prevState,
                    [name]: { ...prevState[name], value, errorMessage: '' }
                };
            }
        });
    };

    const handleAnticipateIssue = (e: React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === 'isAnticipateIssuesWithYourAppeal' && value === sdabFormOptYes) {
            setAnticipateIssues(prevState => {
                return {
                    ...prevState,
                    [name]: { ...prevState[name], value },
                    isAnticipateIssuesWithYourAppealDescription: {
                        ...prevState.isAnticipateIssuesWithYourAppealDescription,
                        isRequired: true
                    }
                };
            });
        } else if (name === 'isAnticipateIssuesWithYourAppeal' && value !== sdabFormOptYes) {
            setAnticipateIssues(prevState => {
                return {
                    ...prevState,
                    [name]: {
                        ...prevState[name],
                        value
                    },
                    isAnticipateIssuesWithYourAppealDescription: {
                        ...prevState.isAnticipateIssuesWithYourAppealDescription,
                        isRequired: false,
                        errorMessage: ''
                    }
                };
            });
        } else if (name === 'isAnticipateIssuesWithYourAppealDescription') {
            if (anticipateIssues.isAnticipateIssuesWithYourAppealDescription.isRequired && value.length <= 0) {
                setAnticipateIssues(prevSt => ({
                    ...prevSt,
                    [name]: { ...prevSt[name], value, errorMessage: `${prevSt[name].label}${requiredErrorSuffix}` }
                }));
            } else {
                setAnticipateIssues(prevSt => ({ ...prevSt, [name]: { ...prevSt[name], value, errorMessage: '' } }));
            }
        }
    };
    const handleBringingWitness = (e: React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === 'bringingWitness' && value === sdabFormOptYes) {
            setBringingWitness(prevState => {
                return {
                    ...prevState,
                    [name]: { ...prevState[name], value },
                    howManyWitness: {
                        ...prevState.howManyWitness,
                        isRequired: true
                    }
                };
            });
        } else if (name === 'bringingWitness' && value !== sdabFormOptYes) {
            setBringingWitness(prevState => {
                return {
                    ...prevState,
                    [name]: {
                        ...prevState[name],
                        value
                    },
                    howManyWitness: {
                        ...prevState.howManyWitness,
                        isRequired: false,
                        errorMessage: ''
                    }
                };
            });
        } else if (name === 'howManyWitness') {
            if (bringingWitness.howManyWitness.isRequired && value.length <= 0) {
                setBringingWitness(prevSt => ({
                    ...prevSt,
                    [name]: { ...prevSt[name], value, errorMessage: `${prevSt[name].label}${requiredErrorSuffix}` }
                }));
            } else {
                setBringingWitness(prevSt => ({ ...prevSt, [name]: { ...prevSt[name], value, errorMessage: '' } }));
            }
        }
    };

    const handleIsUseCustomerInfoChecked = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { checked } = e.target;
        setIsUseCustomerInfoChecked(checked);
    };

    const handleAppealAgainst = (
        e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLSelectElement>
    ) => {
        const { name, value } = e.target;
        let developmentPermit: string, subdivisionPermit: string, noticeOrder: string;
        developmentPermit = subdivisionPermit = noticeOrder = '';
        if (value.includes('Development')) {
            developmentPermit = value;
        } else if (value.includes('Order')) {
            noticeOrder = value;
        } else {
            subdivisionPermit = value;
        }
        //     AppealAgainstDevelopment?: string | undefined |boolean;
        // AppealAgainstSubDivisionApplication?: string | undefined | boolean;
        // AppealAgainstNoticOrder?: string | undefined | boolean;
        setAppealAgainst((prevState: IAppealAgainst) => {
            if ((!value || value.length <= 0) && prevState[name].isRequired) {
                // update error message
                return {
                    ...prevState,
                    [name]: { ...prevState[name], value, errorMessage: `${prevState[name].label} ${requiredErrorSuffix}` }
                };
            } else {
                return {
                    ...prevState,
                    [name]: {
                        ...prevState[name],
                        value,
                        errorMessage: '',
                        AppealAgainstDevelopment: developmentPermit,
                        AppealAgainstSubDivisionApplication: subdivisionPermit,
                        AppealAgainstNoticOrder: noticeOrder
                    }
                };
            }
        });
    };

    const handleFormValidation = async () => {
        //1- Clear previous error message
        //2- Check all required fields are filled for all sections
        setIsDisabled(true);
        let hasError = false;
        for (const [key, property] of Object.entries(customerInfo)) {
            if (!property.value && property.value.length <= 0 && customerInfo[key].isRequired) {
                hasError = true;
                setCustomerInfo(prevState => {
                    return {
                        ...prevState,
                        [key]: { ...prevState[key], errorMessage: `${prevState[key].label}${requiredErrorSuffix}` }
                    };
                });
            }
        }
        for (const [key, property] of Object.entries(appealAgainst)) {
            if (!property.value && property.value.length <= 0 && appealAgainst[key].isRequired) {
                hasError = true;
                setAppealAgainst(prevState => {
                    return {
                        ...prevState,
                        [key]: { ...prevState[key], errorMessage: `${prevState[key].label}${requiredErrorSuffix}` }
                    };
                });
            }
        }
        if (
            anticipateIssues.isAnticipateIssuesWithYourAppealDescription.isRequired &&
            anticipateIssues.isAnticipateIssuesWithYourAppealDescription.value.length <= 0
        ) {
            hasError = true;
            setAnticipateIssues(prevSt => ({
                ...prevSt,
                isAnticipateIssuesWithYourAppealDescription: {
                    ...prevSt.isAnticipateIssuesWithYourAppealDescription,
                    errorMessage: `${prevSt.isAnticipateIssuesWithYourAppealDescription.label}${requiredErrorSuffix}`
                }
            }));
        }
        if (bringingWitness.howManyWitness.isRequired && bringingWitness.howManyWitness.value.length <= 0) {
            hasError = true;
            setBringingWitness(prevSt => ({
                ...prevSt,
                howManyWitness: {
                    ...prevSt.howManyWitness,
                    errorMessage: `${prevSt.howManyWitness.label}${requiredErrorSuffix}`
                }
            }));
        }

        // validation of postal code for (customerInfo and appelantInformation sections)
        if (customerInfo.postalCode && customerInfo.postalCode.value.length > 0) {
            if (!checkCanadianPostalCode(customerInfo.postalCode.value)) {
                hasError = true;
                setCustomerInfo(prevSt => ({
                    ...prevSt,
                    postalCode: { ...prevSt.postalCode, errorMessage: `${invalidErrorPrefix}${prevSt.postalCode.label}` }
                }));
            }
        }
        // validation of residentialPhoneNumber for (customerInfo and appelantInformation sections)
        if (customerInfo.residentialPhone && customerInfo.residentialPhone.value.length > 0) {
            if (!validatePhoneNumber(customerInfo.residentialPhone.value)) {
                hasError = true;
                setCustomerInfo(prevSt => ({
                    ...prevSt,
                    residentialPhone: {
                        ...prevSt.residentialPhone,
                        errorMessage: `${invalidErrorPrefix}${prevSt.residentialPhone.label}.`
                    }
                }));
            }
        }
        // Validate the email address & matching for customerInfo and appelantInfo sections
        //validate the email address (customerInfoSection) and store it's result
        let isValidEmailAddressCustomerInfoSection;
        let isValidConfirmEmailAddressCustomerInfoSection;
        if (customerInfo.email.value.length > 0) {
            if (!validateEmail(customerInfo.email.value)) {
                hasError = true;
                isValidEmailAddressCustomerInfoSection = false;
                setCustomerInfo(prevSt => ({
                    ...prevSt,
                    email: { ...prevSt.email, errorMessage: `${invalidErrorPrefix}${prevSt.email.label}` }
                }));
            } else {
                isValidEmailAddressCustomerInfoSection = true;
                setCustomerInfo(prevSt => ({ ...prevSt, email: { ...prevSt.email, errorMessage: '' } }));
            }
        }
        if (customerInfo.emailConfirm.value.length > 0) {
            if (!validateEmail(customerInfo.emailConfirm.value)) {
                hasError = true;
                isValidConfirmEmailAddressCustomerInfoSection = false;
                setCustomerInfo(prevSt => ({
                    ...prevSt,
                    emailConfirm: { ...prevSt.emailConfirm, errorMessage: `${invalidErrorPrefix}${prevSt.emailConfirm.label}` }
                }));
            } else {
                isValidConfirmEmailAddressCustomerInfoSection = true;
                setCustomerInfo(prevSt => ({ ...prevSt, emailConfirm: { ...prevSt.emailConfirm, errorMessage: '' } }));
            }
        }
        // After validating the EmailAddress now Match the email & emailConfirm and show the error message under emailConfirm address section
        if (
            customerInfo.email.value.length > 0 &&
            customerInfo.emailConfirm.value.length > 0 &&
            isValidConfirmEmailAddressCustomerInfoSection &&
            isValidEmailAddressCustomerInfoSection
        ) {
            if (customerInfo.email.value.toLowerCase() !== customerInfo.emailConfirm.value.toLowerCase()) {
                hasError = true;
                setCustomerInfo(prevSt => ({
                    ...prevSt,
                    emailConfirm: { ...prevSt.emailConfirm, errorMessage: `${prevSt.emailConfirm.label}${matchErrorSuffix}` }
                }));
            } else {
                setCustomerInfo(prevSt => ({ ...prevSt, emailConfirm: { ...prevSt.emailConfirm, errorMessage: '' } }));
            }
        } else {
            // if address is valid then don't display the error for email section
            if (customerInfo.email.value.length > 0 && isValidEmailAddressCustomerInfoSection) {
                setCustomerInfo(prevSt => ({ ...prevSt, email: { ...prevSt.email, errorMessage: '' } }));
            } else {
                hasError = true;
                setCustomerInfo(prevSt => ({
                    ...prevSt,
                    email: { ...prevSt.email, errorMessage: `${invalidErrorPrefix}${prevSt.email.label}` }
                }));
            }

            if (customerInfo.emailConfirm.value.length > 0 && isValidConfirmEmailAddressCustomerInfoSection) {
                setCustomerInfo(prevSt => ({ ...prevSt, emailConfirm: { ...prevSt.emailConfirm, errorMessage: '' } }));
            } else {
                hasError = true;
                setCustomerInfo(prevSt => ({
                    ...prevSt,
                    emailConfirm: { ...prevSt.emailConfirm, errorMessage: `${invalidErrorPrefix}${prevSt.emailConfirm.label}` }
                }));
            }
        }
        // Same validation for email-address for appelantInformation Section

        // End of Email Address validation for Appelant informaiton section
        if (appealAgainst.reasonOfAppeal.value.length > 2000) {
            hasError = true;
            setAppealAgainst(prevSt => {
                return {
                    ...prevSt,
                    reasonOfAppeal: {
                        ...prevSt.reasonOfAppeal,
                        errorMessage: `${appealReasonLengthError}`
                    }
                };
            });
        }

        if (appelantInformation.agentName.isRequired && !appelantInformation.agentName.value.length) {
            hasError = true;
            setAppelantInformation(prevState => {
                return {
                    ...prevState,
                    agentName: { ...prevState.agentName, errorMessage: `${prevState.agentName.label}${requiredErrorSuffix}` }
                };
            });
        }
        if (!isUseCustomerInfoChecked) {
            for (const [key, property] of Object.entries(appelantInformation)) {
                if (!property.value && property.value.length <= 0 && appelantInformation[key].isRequired) {
                    hasError = true;
                    setAppelantInformation(prevState => {
                        return {
                            ...prevState,
                            [key]: { ...prevState[key], errorMessage: `${prevState[key].label}${requiredErrorSuffix}` }
                        };
                    });
                }
            }
            if (appelantInformation.residentialPhone && appelantInformation.residentialPhone.value.length > 0) {
                if (!validatePhoneNumber(appelantInformation.residentialPhone.value)) {
                    hasError = true;
                    setAppelantInformation(prevSt => ({
                        ...prevSt,
                        residentialPhone: {
                            ...prevSt.residentialPhone,
                            errorMessage: `${invalidErrorPrefix}${prevSt.residentialPhone.label}.`
                        }
                    }));
                }
            }

            if (appelantInformation.postalCode && appelantInformation.postalCode.value.length > 0) {
                if (!checkCanadianPostalCode(appelantInformation.postalCode.value)) {
                    hasError = true;
                    setAppelantInformation(prevSt => ({
                        ...prevSt,
                        postalCode: { ...prevSt.postalCode, errorMessage: `${invalidErrorPrefix}${prevSt.postalCode.label}` }
                    }));
                }
            }
            // Validate the email address & matching for customerInfo and appelantInfo sections
            //validate the email address (customerInfoSection) and store it's result
            let isValidEmailAddressAppelantInfoSection;
            let isValidConfirmEmailAddressAppelantInfoSection;

            if (appelantInformation.email.value.length > 0) {
                if (!validateEmail(appelantInformation.email.value)) {
                    hasError = true;
                    isValidEmailAddressAppelantInfoSection = false;
                    setAppelantInformation(prevSt => ({
                        ...prevSt,
                        email: { ...prevSt.email, errorMessage: `${invalidErrorPrefix}${prevSt.email.label}` }
                    }));
                } else {
                    isValidEmailAddressAppelantInfoSection = true;
                    setAppelantInformation(prevSt => ({ ...prevSt, email: { ...prevSt.email, errorMessage: '' } }));
                }
            }

            if (appelantInformation.emailConfirm.value.length > 0) {
                if (!validateEmail(appelantInformation.emailConfirm.value)) {
                    hasError = true;
                    isValidConfirmEmailAddressAppelantInfoSection = false;
                    setAppelantInformation(prevSt => ({
                        ...prevSt,
                        emailConfirm: { ...prevSt.emailConfirm, errorMessage: `${invalidErrorPrefix}${prevSt.emailConfirm.label}` }
                    }));
                } else {
                    isValidConfirmEmailAddressAppelantInfoSection = true;
                    setAppelantInformation(prevSt => ({ ...prevSt, emailConfirm: { ...prevSt.emailConfirm, errorMessage: '' } }));
                }
            }
            // After validating the EmailAddress now Match the email & emailConfirm and show the error message under emailConfirm address section
            if (
                appelantInformation.email.value.length > 0 &&
                appelantInformation.emailConfirm.value.length > 0 &&
                isValidEmailAddressAppelantInfoSection &&
                isValidConfirmEmailAddressAppelantInfoSection
            ) {
                if (appelantInformation.email.value.toLowerCase() !== appelantInformation.emailConfirm.value.toLowerCase()) {
                    hasError = true;
                    setAppelantInformation(prevSt => ({
                        ...prevSt,
                        emailConfirm: { ...prevSt.emailConfirm, errorMessage: `${prevSt.emailConfirm.label}${matchErrorSuffix}` }
                    }));
                } else {
                    setAppelantInformation(prevSt => ({ ...prevSt, emailConfirm: { ...prevSt.emailConfirm, errorMessage: '' } }));
                }
            } else {
                // if address is valid then don't display the error for email section
                if (appelantInformation.email.value.length > 0 && isValidEmailAddressAppelantInfoSection) {
                    setAppelantInformation(prevSt => ({ ...prevSt, email: { ...prevSt.email, errorMessage: '' } }));
                } else {
                    hasError = true;
                    setAppelantInformation(prevSt => ({
                        ...prevSt,
                        email: { ...prevSt.email, errorMessage: `${invalidErrorPrefix}${prevSt.email.label}` }
                    }));
                }

                if (appelantInformation.emailConfirm.value.length > 0 && isValidConfirmEmailAddressAppelantInfoSection) {
                    setAppelantInformation(prevSt => ({ ...prevSt, emailConfirm: { ...prevSt.emailConfirm, errorMessage: '' } }));
                } else {
                    hasError = true;
                    setAppelantInformation(prevSt => ({
                        ...prevSt,
                        emailConfirm: { ...prevSt.emailConfirm, errorMessage: `${invalidErrorPrefix}${prevSt.emailConfirm.label}` }
                    }));
                }
            }
        }
        // If we dont' have any errors proceed with add product to cart and attributes
        if (!hasError) {
            // update the AllAddress (Parent Hook coming from buybox.view.tsx)
            const sdabData: ISDABForm = {
                customerInformation: customerInfo,
                appelantInformation: appelantInformation,
                appealAgainst: appealAgainst,
                anticipateIssues: anticipateIssues,
                witness: bringingWitness,
                isChecked: true
            };
            // Add product to cart.
            await props.addProductToCartWithAttribute(
                [
                    {
                        addressVerified: {},
                        serviceAddress: {},
                        sdab: sdabData,
                        legalDescriptionCondoVerified: {},
                        lookForVerified: {},
                        lcsab: {},
                        legalDescriptionVerified: {},
                        propertyType: PropertyType.sdab,
                        relatedProduct: [product]
                    }
                ],
                restProps.context
            );
            setIsDisabled(false);
            // Clear hooks/state to initial form
            setAppelantInformation(appelantInformationInitialData);
            setCustomerInfo(customerInformationInitialData);
            setAppealAgainst(appealAgainstInitialData);
            setAnticipateIssues(anticipateIssuesInitialData);
            setBringingWitness(bringingWitnessInitialData);
            // close the modal
            toggleSDABModal(false);
        } else {
            setIsDisabled(false);
        }
        // Need to check for email & email-confirm match update error message
        // Need to check for appeal reason required should be less than 2000 characters update error message
        // Need to check for postal code, residential phone validation update error message
    };
    const validateEmail = (email: string) => {
        const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
        return re.test(String(email).toLowerCase());
    };
    const checkCanadianPostalCode = (postalCode: string) => {
        const postalCodeRegex = new RegExp(/^[ABCEGHJKLMNPRSTVXY]\d[ABCEGHJKLMNPRSTVXY][ -]?\d[ABCEGHJKLMNPRSTVXY]\d$/i);

        return postalCodeRegex.test(postalCode);
    };

    const validatePhoneNumber = (phoneNumber: string) => {
        let phoneNumberRegex = /^\d\d\d-\d\d\d-\d\d\d\d$/i;
        return phoneNumberRegex.test(phoneNumber);
    };

    const renderErrorSummary = () => {
        const errors: IErrorSummary[] = [];
        for (const [_, property] of Object.entries(customerInfo)) {
            if (property.errorMessage) {
                errors.push({ errorMessage: property.errorMessage, section: property.section });
            }
        }
        for (const [_, property] of Object.entries(appelantInformation)) {
            if (property.errorMessage) {
                errors.push({ errorMessage: property.errorMessage, section: property.section });
            }
        }
        for (const [_, property] of Object.entries(appealAgainst)) {
            if (property.errorMessage) {
                errors.push({ errorMessage: property.errorMessage, section: property.section });
            }
        }
        if (anticipateIssues.isAnticipateIssuesWithYourAppealDescription.errorMessage) {
            errors.push({
                errorMessage: anticipateIssues.isAnticipateIssuesWithYourAppealDescription.errorMessage,
                section: anticipateIssues.isAnticipateIssuesWithYourAppealDescription.section
            });
        }
        if (bringingWitness.howManyWitness.errorMessage) {
            errors.push({
                errorMessage: bringingWitness.howManyWitness.errorMessage,
                section: bringingWitness.howManyWitness.section
            });
        }
        return errors.map(error => {
            return (
                <li>
                    <a href={`#${error.section}`}>{error.errorMessage}</a>
                </li>
            );
        });
    };
    const closeSDABModal = () => {
        setIsConfirmationModalOpen(true);
        // if (window.confirm('Are you sure you want to close?')) {
        // alert("Are you sure you want to closee?") && toggleSDABModal(false);
        // alert("Are you sure you want to close?") && toggleSDABModal(false);
        // }
    };
    return (
        <Modal
            role='document'
            aria-labelledby='productSearchTitle'
            className='cui modal-xl'
            isOpen={isModelOpen}
            toggle={closeSDABModal}
            keyboard={true}
        >
            <div className='modal-header'>
                <h2 id='productSearchTitle' tabIndex={0} className='modal-title'>
                    {sdabFormHeader}
                </h2>
                <button className='btn-modal-close cui btn-md primary-text' onClick={closeSDABModal}>
                    {closeModalLabel}
                </button>
            </div>
            <ModalBody>
                <div className='property-search-wrap'>
                    <ConfirmationModal
                        isModalOpen={isConfirmationModalOpen}
                        setIsConfirmationModalOpen={setIsConfirmationModalOpen}
                        toggleSDABModal={toggleSDABModal}
                        restProps={props.restProps}
                    />
                    <div className='cui alert-banner inline caution'>
                        <div className='alert-body'>
                            <span className='alert-icon cicon-exclamation-triangle'></span>
                            <p>{sdabFormText}</p>
                        </div>
                    </div>
                    <div className='cui alert-banner inline caution'>
                        <div className='alert-body'>
                            <span className='alert-icon cicon-exclamation-triangle'></span>
                            <p>
                                <RichTextComponent text={formSubmissionAlert} />
                            </p>
                        </div>
                    </div>
                    <small className='info'>{sdabInfoText}</small>
                    <h3>{sdabCustomerInfoLabel}</h3>
                    <form className='form-wrap' id='customerInfo'>
                        <div className='form-group'>
                            <div className='row'>
                                <div className='col-sm-6'>
                                    <label htmlFor='firstName' className='req'>
                                        {sdabCusInfoFirstName}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <input
                                        className={`form-field ${customerInfo.firstName.errorMessage && 'error'}`}
                                        type='text'
                                        name='firstName'
                                        id='firstName'
                                        aria-describedby='firstNameEr'
                                        onChange={handleCustomerInfo}
                                        value={customerInfo.firstName.value}
                                    />
                                    {customerInfo.firstName.errorMessage && (
                                        <div id='firstNameEr' className='cui alert-small error'>
                                            {customerInfo.firstName.errorMessage}
                                        </div>
                                    )}
                                </div>
                                <div className='col-sm-6'>
                                    <label htmlFor='lastName' className='req'>
                                        {sdabCusInfoLastName}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <input
                                        className={`form-field ${customerInfo.lastName.errorMessage && 'error'}`}
                                        type='text'
                                        name='lastName'
                                        id='lastName'
                                        aria-details='lastNameEr'
                                        aria-describedby='lastName'
                                        onChange={handleCustomerInfo}
                                        value={customerInfo.lastName.value}
                                    />
                                    {customerInfo.lastName.errorMessage && (
                                        <div id='lastNameEr' className='cui alert-small error'>
                                            {customerInfo.lastName.errorMessage}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-sm-6'>
                                    <label htmlFor='email' className='req'>
                                        {sdabCusInfoEmail}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <input
                                        className={`form-field ${customerInfo.email.errorMessage && 'error'}`}
                                        type='email'
                                        name='email'
                                        id='email'
                                        aria-describedby='email'
                                        onChange={handleCustomerInfo}
                                        value={customerInfo.email.value}
                                    />
                                    {customerInfo.email.errorMessage && (
                                        <div id='email' className='cui alert-small error'>
                                            {customerInfo.email.errorMessage}
                                        </div>
                                    )}
                                </div>
                                <div className='col-sm-6'>
                                    <label htmlFor='emailConfirm' className='req'>
                                        {sdabCusInfoEmailConf}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <input
                                        className={`form-field ${customerInfo.emailConfirm.errorMessage && 'error'}`}
                                        type='email'
                                        name='emailConfirm'
                                        id='emailConfirm'
                                        aria-describedby='emailConfirmEr'
                                        onChange={handleCustomerInfo}
                                        value={customerInfo.emailConfirm.value}
                                    />
                                    {customerInfo.emailConfirm.errorMessage && (
                                        <div id='emailConfirmEr' className='cui alert-small error'>
                                            {customerInfo.emailConfirm.errorMessage}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-sm-6'>
                                    <label htmlFor='address1' className='req'>
                                        {sdabCusInfoAddress1}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <input
                                        className={`form-field ${customerInfo.address1.errorMessage && 'error'}`}
                                        type='text'
                                        name='address1'
                                        id='address1'
                                        aria-describedby='address1Er'
                                        onChange={handleCustomerInfo}
                                        value={customerInfo.address1.value}
                                    />
                                    {customerInfo.address1.errorMessage && (
                                        <div id='address1' className='cui alert-small error'>
                                            {customerInfo.address1.errorMessage}
                                        </div>
                                    )}
                                </div>
                                <div className='col-sm-4'>
                                    <label htmlFor='address2'>{sdabCusInfoAddress2}</label>
                                    <input
                                        className={`form-field ${customerInfo.address2.errorMessage && 'error'}`}
                                        type='text'
                                        name='address2'
                                        id='address2'
                                        aria-describedby='address2Er'
                                        onChange={handleCustomerInfo}
                                        value={customerInfo.address2.value}
                                    />
                                    {customerInfo.address2.errorMessage && (
                                        <div id='address2Er' className='cui alert-small error'>
                                            {customerInfo.address2.errorMessage}
                                        </div>
                                    )}
                                </div>
                                <div className='col-sm-2'>
                                    <label htmlFor='city' className='req'>
                                        {sdabCusInfoCity}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <input
                                        className={`form-field ${customerInfo.city.errorMessage && 'error'}`}
                                        type='text'
                                        name='city'
                                        id='city'
                                        aria-describedby='cityEr'
                                        onChange={handleCustomerInfo}
                                        value={customerInfo.city.value}
                                    />
                                    {customerInfo.city.errorMessage && (
                                        <div id='cityEr' className='cui alert-small error'>
                                            {customerInfo.city.errorMessage}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-sm-6'>
                                    <label htmlFor='province' className='req'>
                                        {sdabCusInfoProvince}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <select
                                        className={`form-field ${customerInfo.province.errorMessage && 'error'}`}
                                        value={customerInfo.province.value}
                                        name='province'
                                        id='province'
                                        aria-describedby='provinceEr'
                                        onChange={handleCustomerInfo}
                                    >
                                        {ALL_PROVINCES.map(province => (
                                            <option key={province} value={province}>
                                                {province}
                                            </option>
                                        ))}
                                    </select>
                                    {customerInfo.province.errorMessage && (
                                        <div id='province' className='cui alert-small error'>
                                            {customerInfo.province.errorMessage}
                                        </div>
                                    )}
                                </div>
                                <div className='col-sm-6'>
                                    <label htmlFor='postalCode' className='req'>
                                        {sdabCusInfoPPostalCode}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <input
                                        className={`form-field ${customerInfo.postalCode.errorMessage && 'error'}`}
                                        type='text'
                                        placeholder='e.g. X9X 9X9'
                                        name='postalCode'
                                        id='postalCode'
                                        aria-describedby='postalCodeEr'
                                        onChange={handleCustomerInfo}
                                        value={customerInfo.postalCode.value}
                                    />
                                    {customerInfo.postalCode.errorMessage && (
                                        <div id='postalCodeEr' className='cui alert-small error'>
                                            {customerInfo.postalCode.errorMessage}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-sm-6'>
                                    <label htmlFor='residentialPhone' className='req'>
                                        {sdabCusInfoPResidentialPhone}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <input
                                        className={`form-field ${customerInfo.residentialPhone.errorMessage && 'error'}`}
                                        type='text'
                                        placeholder='e.g. 555-555-5555'
                                        name='residentialPhone'
                                        id='residentialPhone'
                                        aria-describedby='residentialPhoneEr'
                                        onChange={handleCustomerInfo}
                                        value={customerInfo.residentialPhone.value}
                                    />
                                    {customerInfo.residentialPhone.errorMessage && (
                                        <div id='residentialPhoneEr' className='cui alert-small error'>
                                            {customerInfo.residentialPhone.errorMessage}
                                        </div>
                                    )}
                                </div>
                                <div className='col-sm-6'>
                                    <label htmlFor='cusInfo' className='req'>
                                        {sdabCusInfoPCountry}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <input
                                        className={`form-field ${customerInfo.country.errorMessage && 'error'}`}
                                        type='text'
                                        id='cusInfo'
                                        value={customerInfo.country.value}
                                        onChange={handleCustomerInfo}
                                        disabled
                                    />
                                </div>
                            </div>
                        </div>
                    </form>
                    <h3>{sdabSiteInfo}</h3>
                    <form className='form-wrap' id='siteInfo'>
                        <div className='form-group'>
                            <div className='row'>
                                <div className='col-sm-6'>
                                    <label htmlFor='municipalAddress' className='req'>
                                        {sdabSiteMunicipal}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <input
                                        className={`form-field ${customerInfo.municipalAddress.errorMessage && 'error'}`}
                                        type='text'
                                        name='municipalAddress'
                                        id='municipalAddress'
                                        aria-describedby='municipalAddressEr'
                                        onChange={handleCustomerInfo}
                                        value={customerInfo.municipalAddress.value}
                                    />
                                    {customerInfo.municipalAddress.errorMessage && (
                                        <div id='municipalAddressEr' className='cui alert-small error'>
                                            {customerInfo.municipalAddress.errorMessage}
                                        </div>
                                    )}
                                </div>
                                <div className='col-sm-6'>
                                    <label htmlFor='otherDocuments' className='req'>
                                        {sdabSiteOtherDoc}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <input
                                        className={`form-field ${customerInfo.otherDocuments.errorMessage && 'error'}`}
                                        type='text'
                                        placeholder={sdabSiteOtherDocPalaceHolder}
                                        name='otherDocuments'
                                        id='otherDocuments'
                                        aria-describedby='otherDocumentsEr'
                                        value={customerInfo.otherDocuments.value}
                                        onChange={handleCustomerInfo}
                                    />
                                    {customerInfo.otherDocuments.errorMessage && (
                                        <div id='otherDocumentsEr' className='cui alert-small error'>
                                            {customerInfo.otherDocuments.errorMessage}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </form>
                    <h3 id='appelantInfo'>{sdabAppellantInfoLabel}</h3>
                    <div className='cui alert-banner inline caution'>
                        <div className='alert-body'>
                            <span className='alert-icon cicon-exclamation-triangle'></span>
                            <strong>{sdabAppellantInfoTxtLbl}</strong>
                            <p>{sdabAppellantInfoTxt}</p>
                        </div>
                    </div>
                    <label htmlFor='useCustomerInformation' className='form-wrap conf-box'>
                        <input
                            type='checkbox'
                            name='useCustomerInformation'
                            id='useCustomerInformation'
                            onChange={handleIsUseCustomerInfoChecked}
                        />
                        {sdabSameInfoCheckBoxLabel}
                    </label>
                    <form className='form-wrap'>
                        <div className='form-group'>
                            <div className='row'>
                                <div className='col-sm-4'>
                                    <label htmlFor='firstName' className='req'>
                                        {sdabCusInfoFirstName}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <input
                                        className={`form-field ${appelantInformation.firstName.errorMessage && 'error'}`}
                                        type='text'
                                        name='firstName'
                                        id='firstName'
                                        aria-describedby='firstNameEr'
                                        onChange={handleAppelantInformation}
                                        value={appelantInformation.firstName.value}
                                        disabled={isUseCustomerInfoChecked}
                                    />
                                    {appelantInformation.firstName.errorMessage && (
                                        <div id='firstNameEr' className='cui alert-small error'>
                                            {appelantInformation.firstName.errorMessage}
                                        </div>
                                    )}
                                </div>
                                <div className='col-sm-4'>
                                    <label htmlFor='lastName' className='req'>
                                        {sdabCusInfoLastName}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <input
                                        className={`form-field ${appelantInformation.lastName.errorMessage && 'error'}`}
                                        type='text'
                                        name='lastName'
                                        id='lastName'
                                        aria-describedby='lastNameEr'
                                        onChange={handleAppelantInformation}
                                        value={appelantInformation.lastName.value}
                                        disabled={isUseCustomerInfoChecked}
                                    />
                                    {appelantInformation.lastName.errorMessage && (
                                        <div id='lastNameEr' className='cui alert-small error'>
                                            {appelantInformation.lastName.errorMessage}
                                        </div>
                                    )}
                                </div>
                                <div className='col-sm-4'>
                                    <label htmlFor='agentName' className='req'>
                                        {sdabAgentNameLabel}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <input
                                        className={`form-field ${appelantInformation.agentName.errorMessage && 'error'}`}
                                        type='text'
                                        name='agentName'
                                        id='agentName'
                                        aria-describedby='agentNameEr'
                                        value={appelantInformation.agentName.value}
                                        onChange={handleAppelantInformation}
                                    />
                                    {appelantInformation.agentName.errorMessage && (
                                        <div id='agentNameEr' className='cui alert-small error'>
                                            {appelantInformation.agentName.errorMessage}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-sm-6'>
                                    <label htmlFor='email' className='req'>
                                        {sdabCusInfoEmail}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <input
                                        className={`form-field ${appelantInformation.email.errorMessage && 'error'}`}
                                        type='email'
                                        name='email'
                                        id='email'
                                        value={appelantInformation.email.value}
                                        onChange={handleAppelantInformation}
                                        disabled={isUseCustomerInfoChecked}
                                    />
                                    {appelantInformation.email.errorMessage && (
                                        <div id='email' className='cui alert-small error'>
                                            {appelantInformation.email.errorMessage}
                                        </div>
                                    )}
                                </div>
                                <div className='col-sm-6'>
                                    <label htmlFor='emailConfirm' className='req'>
                                        {sdabCusInfoEmailConf}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <input
                                        className={`form-field ${appelantInformation.emailConfirm.errorMessage && 'error'}`}
                                        type='email'
                                        name='emailConfirm'
                                        id='emailConfirm'
                                        aria-describedby='emailConfirmEr'
                                        onChange={handleAppelantInformation}
                                        value={appelantInformation.emailConfirm.value}
                                        disabled={isUseCustomerInfoChecked}
                                    />
                                    {appelantInformation.emailConfirm.errorMessage && (
                                        <div id='emailConfirmEr' className='cui alert-small error'>
                                            {appelantInformation.emailConfirm.errorMessage}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-sm-6'>
                                    <label htmlFor='address1' className='req'>
                                        {sdabCusInfoAddress1}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <input
                                        className={`form-field ${appelantInformation.address1.errorMessage && 'error'}`}
                                        type='text'
                                        name='address1'
                                        id='address1'
                                        aria-describedby='address1Er'
                                        value={appelantInformation.address1.value}
                                        onChange={handleAppelantInformation}
                                        disabled={isUseCustomerInfoChecked}
                                    />
                                    {appelantInformation.address1.errorMessage && (
                                        <div id='address1Er' className='cui alert-small error'>
                                            {appelantInformation.address1.errorMessage}
                                        </div>
                                    )}
                                </div>
                                <div className='col-sm-4'>
                                    <label htmlFor='address2'>{sdabCusInfoAddress2}</label>
                                    <input
                                        className={`form-field ${appelantInformation.address2.errorMessage && 'error'}`}
                                        type='text'
                                        name='address2'
                                        id='address2'
                                        aria-describedby='address2Er'
                                        value={appelantInformation.address2.value}
                                        onChange={handleAppelantInformation}
                                        disabled={isUseCustomerInfoChecked}
                                    />
                                    {appelantInformation.address2.errorMessage && (
                                        <div id='address2Er' className='cui alert-small error'>
                                            {appelantInformation.address2.errorMessage}
                                        </div>
                                    )}
                                </div>
                                <div className='col-sm-2'>
                                    <label htmlFor='city' className='req'>
                                        {sdabCusInfoCity}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <input
                                        className={`form-field ${appelantInformation.city.errorMessage && 'error'}`}
                                        type='text'
                                        name='city'
                                        id='city'
                                        aria-describedby='cityEr'
                                        value={appelantInformation.city.value}
                                        onChange={handleAppelantInformation}
                                        disabled={isUseCustomerInfoChecked}
                                    />
                                    {appelantInformation.city.errorMessage && (
                                        <div id='cityEr' className='cui alert-small error'>
                                            {appelantInformation.city.errorMessage}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-sm-4'>
                                    <label htmlFor='province' className='req'>
                                        {sdabCusInfoProvince}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <select
                                        className={`form-field ${appelantInformation.province.errorMessage && 'error'}`}
                                        value={appelantInformation.province.value}
                                        name='province'
                                        id='province'
                                        aria-describedby='provinceEr'
                                        onChange={handleAppelantInformation}
                                        disabled={isUseCustomerInfoChecked}
                                    >
                                        {ALL_PROVINCES.map(province => (
                                            <option key={province} value={province}>
                                                {province}
                                            </option>
                                        ))}
                                    </select>
                                    {appelantInformation.province.errorMessage && (
                                        <div id='provinceEr' className='cui alert-small error'>
                                            {appelantInformation.province.errorMessage}
                                        </div>
                                    )}
                                </div>
                                <div className='col-sm-4'>
                                    <label htmlFor='postalCode' className='req'>
                                        {sdabCusInfoPPostalCode}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <input
                                        className={`form-field ${appelantInformation.postalCode.errorMessage && 'error'}`}
                                        type='text'
                                        placeholder={sdabCusInfoPPostalCodePlaceHolder}
                                        name='postalCode'
                                        id='postalCode'
                                        aria-describedby='postalCodeEr'
                                        value={appelantInformation.postalCode.value}
                                        onChange={handleAppelantInformation}
                                        disabled={isUseCustomerInfoChecked}
                                    />
                                    {appelantInformation.postalCode.errorMessage && (
                                        <div id='postalCodeEr' className='cui alert-small error'>
                                            {appelantInformation.postalCode.errorMessage}
                                        </div>
                                    )}
                                </div>
                                <div className='col-sm-4'>
                                    <label htmlFor='residentialPhone' className='req'>
                                        {sdabCusInfoPResidentialPhone}
                                        <span className='starick'>{requiredText}</span>
                                    </label>
                                    <input
                                        className={`form-field ${appelantInformation.residentialPhone.errorMessage && 'error'}`}
                                        type='text'
                                        placeholder={sdabCusInfoPResidentialPhonePlaceHolder}
                                        name='residentialPhone'
                                        id='residentialPhone'
                                        aria-describedby='residentialPhoneEr'
                                        value={appelantInformation.residentialPhone.value}
                                        onChange={handleAppelantInformation}
                                        disabled={isUseCustomerInfoChecked}
                                    />
                                    {appelantInformation.residentialPhone.errorMessage && (
                                        <div id='residentialPhoneEr' className='cui alert-small error'>
                                            {appelantInformation.residentialPhone.errorMessage}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </form>
                    <h3>
                        {sdabAppellantAgainstLabel}
                        <span className='starick'>{requiredText}</span>
                    </h3>
                    <div className='cui alert-banner inline information'>
                        <div className='alert-body'>
                            <span className='alert-icon cicon-exclamation-circle'></span>
                            <strong>{sdabAppellantAgainstInfoLabel}</strong>
                            <p>{sdabAppellantAgainstInfoTxt}</p>
                        </div>
                    </div>
                    <form className='form-wrap' action='appeals'>
                        {/* Section Appeal Type */}
                        <div className='option-list' id='appealType'>
                            <h4>{sdabAppellantThDevelopmentPermit}</h4>
                            <ul>
                                <li>
                                    <label htmlFor='appeal1'>
                                        <input
                                            name='appeal'
                                            id='appeal1'
                                            type='radio'
                                            value={'Approval of a Development Permit'}
                                            onChange={handleAppealAgainst}
                                        />
                                        {sdabAppellantTdApproval}
                                    </label>
                                </li>
                                <li>
                                    <label htmlFor='appeal2'>
                                        <input
                                            name='appeal'
                                            id='appeal2'
                                            type='radio'
                                            value={'Conditions of Development Permit Approval'}
                                            onChange={handleAppealAgainst}
                                        />
                                        {sdabAppellantTdConditionsApproval}
                                    </label>
                                </li>
                                <li>
                                    <label htmlFor='appeal3'>
                                        <input
                                            name='appeal'
                                            id='appeal3'
                                            type='radio'
                                            value={'Refusal of a Development Permit'}
                                            onChange={handleAppealAgainst}
                                        />
                                        {sdabAppellantTdRefusal}
                                    </label>
                                </li>
                            </ul>
                        </div>
                        <div className='option-list'>
                            <h4>{sdabAppellantThSAA}</h4>
                            <ul>
                                <li>
                                    <label htmlFor='appeal4'>
                                        <input name='appeal' id='appeal4' type='radio' value={'Approval'} onChange={handleAppealAgainst} />
                                        {sdabAppellantTdApproval}
                                    </label>
                                </li>
                                <li>
                                    <label htmlFor='appeal5'>
                                        <input
                                            name='appeal'
                                            id='appeal5'
                                            type='radio'
                                            value={'Conditions of Subdivision Application Approval'}
                                            onChange={handleAppealAgainst}
                                        />
                                        {sdabAppellantTdConditionsApproval}
                                    </label>
                                </li>
                                <li>
                                    <label htmlFor='appeal6'>
                                        <input
                                            name='appeal'
                                            id='appeal6'
                                            type='radio'
                                            value={'Refusal of a Subdivision Application'}
                                            onChange={handleAppealAgainst}
                                        />
                                        {sdabAppellantTdRefusal}
                                    </label>
                                </li>
                            </ul>
                        </div>
                        <div className='option-list'>
                            <h4>{sdabAppellantThNoticeOrder}</h4>
                            <ul>
                                <li>
                                    <label htmlFor='appeal7'>
                                        <input name='appeal' id='appeal7' type='radio' value={'Order'} onChange={handleAppealAgainst} />
                                        {sdabAppellantTdNoticeOrder}
                                    </label>
                                </li>
                            </ul>
                        </div>
                        {appealAgainst.appeal.errorMessage && (
                            <div
                                aria-live='assertive'
                                aria-label={appealAgainst.appeal.errorMessage}
                                role='alert'
                                className='cui alert-small error'
                            >
                                {appealAgainst.appeal.errorMessage}
                            </div>
                        )}
                    </form>
                    <form className='form-wrap'>
                        <div className='form-group'>
                            <label htmlFor='reasonOfAppeal'>
                                {sdabReasonAppealLabel}
                                <span className='starick'>{requiredText}</span>
                            </label>
                            <p>{sdabReasonAppealText1}</p>

                            <textarea
                                className={`form-field ${appealAgainst.reasonOfAppeal.errorMessage && 'error'}`}
                                name='reasonOfAppeal'
                                id='reasonOfAppeal'
                                aria-aria-describedby='reasonOfAppealEr'
                                value={appealAgainst.reasonOfAppeal.value}
                                onChange={handleAppealAgainst}
                                maxLength={1500}
                            ></textarea>
                            {appealAgainst.reasonOfAppeal.errorMessage && (
                                <div id='reasonOfAppealEr' className='cui alert-small error'>
                                    {appealAgainst.reasonOfAppeal.errorMessage}
                                </div>
                            )}
                            <strong className='characterNum'>
                                <input className='form-field' type='text' value={1500 - appealAgainst.reasonOfAppeal.value.length} />
                                {sdabFormCharacterNum}
                            </strong>
                        </div>
                    </form>
                    <p>{sdabFormSchedulingText}</p>
                    <form className='form-wrap'>
                        <div className='form-group'>
                            <div className='row'>
                                <div className='col-sm-6'>
                                    <label htmlFor='presentationTime'>{sdabFormEstimateLabel}</label>
                                    <select
                                        className={`form-field ${appealAgainst.presentationTime.errorMessage && 'error'}`}
                                        name='presentationTime'
                                        id='presentationTime'
                                        aria-describedby='presentationTimeEr'
                                        value={appealAgainst.presentationTime.value}
                                        onChange={handleAppealAgainst}
                                    >
                                        <option value={sdabFormEstimateOption30}>{sdabFormEstimateOption30}</option>
                                        <option value={sdabFormEstimateOption1h}>{sdabFormEstimateOption1h}</option>
                                        <option value={sdabFormEstimateOption15h}>{sdabFormEstimateOption15h}</option>
                                        <option value={sdabFormEstimateOption2h}>{sdabFormEstimateOption2h}</option>
                                        <option value={sdabFormEstimateOption2PlusH}>{sdabFormEstimateOption2PlusH}</option>
                                    </select>
                                    {appealAgainst.presentationTime.errorMessage && (
                                        <div id='presentationTimeEr' className='cui alert-small error'>
                                            {appealAgainst.presentationTime.errorMessage}
                                        </div>
                                    )}
                                </div>
                                <div className='col-sm-6'>
                                    <label>{sdabFormLegalCounselLabel}</label>
                                    <form action='counsel'>
                                        <div className='form-check form-check-inline'>
                                            <label htmlFor='usingAgentOrLegalCounsel' className='form-check-label'>
                                                <input
                                                    name='usingAgentOrLegalCounsel'
                                                    id='usingAgentOrLegalCounsel'
                                                    value={sdabFormOptYes}
                                                    onChange={handleAppealAgainst}
                                                    className='form-check-input'
                                                    type='radio'
                                                />{' '}
                                                {sdabFormOptYes}
                                            </label>
                                        </div>
                                        <div className='form-check form-check-inline'>
                                            <label htmlFor='usingAgentOrLegalCounsel' className='form-check-label'>
                                                <input
                                                    name='usingAgentOrLegalCounsel'
                                                    id='usingAgentOrLegalCounsel'
                                                    onChange={handleAppealAgainst}
                                                    value={sdabFormOptNo}
                                                    className='form-check-input'
                                                    type='radio'
                                                />{' '}
                                                {sdabFormOptNo}
                                            </label>
                                        </div>
                                        <div className='form-check form-check-inline'>
                                            <label htmlFor='usingAgentOrLegalCounsel' className='form-check-label'>
                                                <input
                                                    name='usingAgentOrLegalCounsel'
                                                    id='usingAgentOrLegalCounsel'
                                                    onChange={handleAppealAgainst}
                                                    value={sdabFormOptUnkown}
                                                    className='form-check-input'
                                                    type='radio'
                                                />
                                                {sdabFormOptUnkown}
                                            </label>
                                        </div>
                                    </form>
                                </div>
                            </div>
                            <div className='row' id='issues'>
                                <div className='col-sm-12'>
                                    <label>{sdabFormAnticipateText}</label>
                                    <form className='form-wrap'>
                                        <div className='form-check form-check-inline'>
                                            <label id='isAnticipateIssuesWithYourAppeal' className='form-check-label'>
                                                <input
                                                    name='isAnticipateIssuesWithYourAppeal'
                                                    id='isAnticipateIssuesWithYourAppeal'
                                                    onChange={handleAnticipateIssue}
                                                    value={sdabFormOptYes}
                                                    className='form-check-input'
                                                    type='radio'
                                                />{' '}
                                                {sdabFormOptYes}
                                            </label>
                                        </div>
                                        <div className='form-check form-check-inline'>
                                            <label htmlFor='isAnticipateIssuesWithYourAppeal' className='form-check-label'>
                                                <input
                                                    name='isAnticipateIssuesWithYourAppeal'
                                                    id='isAnticipateIssuesWithYourAppeal'
                                                    onChange={handleAnticipateIssue}
                                                    value={sdabFormOptNo}
                                                    className='form-check-input'
                                                    type='radio'
                                                />{' '}
                                                {sdabFormOptNo}
                                            </label>
                                        </div>
                                        <div className='form-check form-check-inline'>
                                            <label htmlFor='isAnticipateIssuesWithYourAppeal' className='form-check-label'>
                                                <input
                                                    name='isAnticipateIssuesWithYourAppeal'
                                                    id='isAnticipateIssuesWithYourAppeal'
                                                    onChange={handleAnticipateIssue}
                                                    value={sdabFormOptUnkown}
                                                    className='form-check-input'
                                                    type='radio'
                                                />
                                                {sdabFormOptUnkown}
                                            </label>
                                        </div>
                                    </form>
                                </div>
                                <div className='col-sm-12'>
                                    <label htmlFor='isAnticipateIssuesWithYourAppealDescription'>{sdabFormCharactersLabel}</label>
                                    {/* If option selected to yes then display the textarea and */}
                                    {anticipateIssues.isAnticipateIssuesWithYourAppeal.value.toLowerCase() ===
                                        sdabFormOptYes.toLowerCase() && (
                                        <>
                                            <textarea
                                                className={`form-field anticipate-issue ${anticipateIssues
                                                    .isAnticipateIssuesWithYourAppealDescription.errorMessage && 'error'}`}
                                                name='isAnticipateIssuesWithYourAppealDescription'
                                                id='isAnticipateIssuesWithYourAppealDescription'
                                                aria-describedby='isAnticipateIssuesWithYourAppealDescriptionEr'
                                                value={anticipateIssues.isAnticipateIssuesWithYourAppealDescription.value}
                                                onChange={handleAnticipateIssue}
                                                maxLength={150}
                                            ></textarea>
                                            {anticipateIssues.isAnticipateIssuesWithYourAppealDescription.isRequired && (
                                                <div id='isAnticipateIssuesWithYourAppealDescriptionEr' className='cui alert-small error'>
                                                    {anticipateIssues.isAnticipateIssuesWithYourAppealDescription.errorMessage}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className='row'>
                                <div className='col-sm-6'>
                                    <label>{sdabFormAnticipateWitnessText}</label>
                                    <form action='anticipate'>
                                        <div className='form-check form-check-inline'>
                                            <label htmlFor='bringingWitness' className='form-check-label'>
                                                <input
                                                    name='bringingWitness'
                                                    id='bringingWitness'
                                                    className='form-check-input'
                                                    onChange={handleBringingWitness}
                                                    value={sdabFormOptYes}
                                                    type='radio'
                                                />{' '}
                                                {sdabFormOptYes}
                                            </label>
                                        </div>
                                        <div className='form-check form-check-inline'>
                                            <label htmlFor='bringingWitness2' className='form-check-label'>
                                                <input
                                                    name='bringingWitness'
                                                    id='bringingWitness2'
                                                    value={sdabFormOptNo}
                                                    onChange={handleBringingWitness}
                                                    className='form-check-input'
                                                    type='radio'
                                                />{' '}
                                                {sdabFormOptNo}
                                            </label>
                                        </div>
                                        <div className='form-check form-check-inline'>
                                            <label htmlFor='bringingWitness3' className='form-check-label'>
                                                <input
                                                    name='bringingWitness'
                                                    id='bringingWitness3'
                                                    value={sdabFormOptUnkown}
                                                    onChange={handleBringingWitness}
                                                    className='form-check-input'
                                                    type='radio'
                                                />
                                                {sdabFormOptUnkown}
                                            </label>
                                        </div>
                                    </form>
                                </div>
                                <div className='col-sm-6'>
                                    <label htmlFor='howManyWitness' className='req'>
                                        {sdabFormBringingLabel} <span className='starick'>{requiredText}</span>
                                    </label>
                                    {bringingWitness.bringingWitness.value.toLowerCase() === sdabFormOptYes.toLowerCase() && (
                                        <>
                                            <input
                                                name='howManyWitness'
                                                id='howManyWitness'
                                                aria-describedby='howManyWitness'
                                                className={`form-field ${bringingWitness.howManyWitness.errorMessage && 'error'}`}
                                                type='text'
                                                value={bringingWitness.howManyWitness.value}
                                                onChange={handleBringingWitness}
                                            />
                                            <div>{bringingWitness.howManyWitness.errorMessage}</div>
                                            {bringingWitness.howManyWitness.errorMessage && (
                                                <div id='howManyWitness' className='cui alert-small error'>
                                                    {bringingWitness.howManyWitness.errorMessage}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </form>
                    <div className='cui alert-banner inline caution'>
                        <div className='alert-body'>
                            <span className='alert-icon cicon-exclamation-circle'></span>
                            <h2 className='alert-title'>{iHereByText}</h2>
                            <ul className='list-style-dot'>
                                <li>
                                    <strong>{underStoodText}</strong>
                                </li>
                                <li>
                                    <strong>{infoProvidedAccurate}</strong>
                                </li>
                                <li>
                                    <strong>{responsiblePayingAppeal}</strong>
                                </li>
                            </ul>
                        </div>
                    </div>
                    <small>{sdabFormDesclaimerText}</small>
                    {/* Error Summary */}
                    <div className='error-Summary'>{renderErrorSummary()}</div>
                </div>
            </ModalBody>
            <ModalFooter>
                <div className='btn-box'>
                    <button onClick={handleFormValidation} type='button' className='cui btn-md primary'>
                        <span className='btn-wrapper'>
                            <span className='btn-text'>{addAppealToCartButtonLabel}</span>
                            <span className='cicon-angle-right right'></span>
                        </span>
                    </button>
                </div>
                {isDisabled ? (
                    <div className='spinner-wrap'>
                        <div className='preload-spinner-container' role='status'>
                            <div className='preload-spinner'></div>
                            <p>{props.restProps.resources.loadingLabel}</p>
                        </div>
                    </div>
                ) : null}
            </ModalFooter>
        </Modal>
    );
};
export default SDABForm;
